import { useEffect, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { ChevronRight, ChevronsUpDown, X } from "lucide-react";
import { useScrollLock } from "usehooks-ts";

import { findDocLocation } from "@/landing/docs/docs-nav";
import { DocsSidebar } from "@/landing/docs/DocsSidebar";

/**
 * Below lg the sidebar folds into a bar under the nav (section and page) that
 * opens the full docs navigation as a drawer from the left.
 */
export function MobileSidebarDrawer() {
  const [open, setOpen] = useState(false);
  const current = findDocLocation(useLocation().pathname ?? "");

  return (
    <>
      <div className="sticky top-(--header-height) z-30 border-b border-(--g-rule-soft) bg-[rgb(227_230_229/86%)] px-(--g-margin) backdrop-blur-xl lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded={open}
          aria-controls="docs-drawer"
          className="flex h-12 w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0 text-left text-[14px]"
        >
          <span className="sr-only">Open docs navigation:</span>
          {current ? (
            <>
              <span className="truncate text-(--g-ink-3)">
                {current.section.title}
              </span>
              <ChevronRight
                aria-hidden
                className="size-3.5 flex-none text-(--g-ink-3)"
              />
              <span className="truncate font-medium text-(--g-ink)">
                {current.item.label}
              </span>
            </>
          ) : (
            <span className="font-medium text-(--g-ink)">Docs</span>
          )}
          <ChevronsUpDown
            aria-hidden
            className="ml-auto size-4 flex-none text-(--g-ink-2)"
          />
        </button>
      </div>

      {open && <Drawer onClose={() => setOpen(false)} />}
    </>
  );
}

function Drawer({ onClose }: { onClose: () => void }) {
  useScrollLock();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Docs navigation"
      className="fixed inset-0 z-[60] lg:hidden"
    >
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-[rgb(22_25_28/30%)]"
      />
      <div
        id="docs-drawer"
        className="absolute inset-y-0 left-0 flex w-[min(320px,86vw)] flex-col bg-(--g-card) shadow-[0_24px_60px_-20px_rgb(0_0_0/45%)]"
      >
        <div className="flex h-(--header-height) flex-none items-center justify-between border-b border-(--g-rule-soft) pr-2.5 pl-(--g-margin)">
          <span className="text-[15px] font-medium text-(--g-ink)">Docs</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close docs navigation"
            className="inline-flex size-9 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--g-ink-2) transition-colors hover:bg-[rgb(22_25_28/6%)] hover:text-(--g-ink)"
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-(--g-margin) pt-4 pb-10">
          <DocsSidebar onNavigate={onClose} />
        </div>
      </div>
    </div>
  );
}
